import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'apollo-react/components/Box';
import CommonModal from '../CommonModal';
import CommentViewer from './CommentViewer';
import CommentForm from './CommentForm';
import styles from './CommentsModal.module.css';

/**
 * CommentsModal - Modal for viewing all comments and adding new ones
 *
 * @param {boolean} open - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {string} title - Modal title
 * @param {Array} comments - Array of comments
 * @param {boolean} enableAddComment - Whether adding comments is enabled
 * @param {boolean} disabled - Whether the form is disabled
 * @param {number|string} subformId - Subform ID for adding comments
 * @param {string} fieldId - Field ID for adding comments
 * @param {Function} onAddComment - Callback to add a new comment
 * @param {Function} onCommentAdded - Callback when comment is successfully added
 */
const CommentsModal = ({
  open,
  onClose,
  title,
  comments = [],
  enableAddComment = false,
  disabled = false,
  subformId,
  fieldId,
  onAddComment,
  onCommentAdded,
  requestProgress,
  requestDateEnded,
  isAdditionalDocument,
  currentUserEmail,
  requestId,
  onCommentUpdated,
  onCommentDeleted,
}) => {
  const { t } = useTranslation();
  const hasComments = comments.length > 0;
  const modalTitle = title || t('comments');

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={modalTitle}
      hideButtons
      className={styles.modal}
    >
      {enableAddComment && !disabled && (
        <Box className={styles.formContainer}>
          <CommentForm
            subFormId={subformId}
            fieldId={fieldId}
            onAddComment={onAddComment}
            onClose={onClose}
            onCommentAdded={onCommentAdded}
            hideCancelButton={false}
            hideTitleSeparator={true}
            compact={false}
          />
        </Box>
      )}

      <Box className={styles.content}>
        {hasComments && (
          <CommentViewer
            comments={comments}
            compact={false}
            hideHeader={false}
            requestProgress={requestProgress}
            requestDateEnded={requestDateEnded}
            isAdditionalDocument={isAdditionalDocument}
            currentUserEmail={currentUserEmail}
            requestId={requestId}
            onCommentUpdated={onCommentUpdated}
            onCommentDeleted={onCommentDeleted}
          />
        )}
      </Box>
    </CommonModal>
  );
};

export default CommentsModal;
